import {AxiosResponse} from "axios";

import ResourceApi from "@/src/modules/core/api/resource-api";
import {axios} from "@/src/modules/core/lib";
import {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

import type {
  AmenityAccessRestrictionMutationResponse,
  DeactivateResidentRequest,
  DeactivateResidentResult,
  Household,
  HouseholdAmenityRestrictionHistoryRow,
  ResidentTurnoverStatus,
} from "../types";
import type {
  HouseholdOccupantMutationResult,
  InviteHouseholdOccupantRequest,
  RevokeHouseholdOccupantRequest,
} from "../types/occupant";
import type {HouseholdStatusInput, HouseholdStatusResult} from "../types/household-status";

const PATH = "household";

class HouseholdApi extends ResourceApi<Household> {
  constructor() {
    super(PATH);
  }

  getHouseholds(options: ParamsOptions): Promise<AxiosResponse<GetListResponse<Household>>> {
    return this.search(options);
  }

  getResidentTurnoverStatus(id: number): Promise<AxiosResponse<{data: ResidentTurnoverStatus}>> {
    return this.get(`households/${id}/resident-turnover/status`);
  }

  deactivateResident(
    id: number,
    body: DeactivateResidentRequest,
  ): Promise<AxiosResponse<{data: DeactivateResidentResult}>> {
    return this.post(`households/${id}/resident-turnover/deactivate`, body);
  }

  downloadResidentWelcomePdf(id: number): Promise<AxiosResponse<Blob>> {
    return axios({
      url: `households/${id}/resident-welcome.pdf`,
      method: "get",
      responseType: "blob",
    });
  }

  updateStatus(
    id: number,
    body: HouseholdStatusInput,
  ): Promise<
    AxiosResponse<{
      success?: number;
      message?: string;
      data: HouseholdStatusResult;
    }>
  > {
    return axios({
      url: `${PATH}/${id}/status`,
      method: "patch",
      data: body,
    });
  }

  postAmenityAccessRestriction(
    id: number,
    body: Record<string, unknown>,
  ): Promise<AxiosResponse<GetResponse<AmenityAccessRestrictionMutationResponse> & {message?: string}>> {
    return this.post(`${PATH}/${id}/amenity-access-restriction`, body);
  }

  inviteOccupant(
    id: number,
    body: InviteHouseholdOccupantRequest,
  ): Promise<AxiosResponse<{data: HouseholdOccupantMutationResult; message?: string}>> {
    return this.post(`households/${id}/occupant/invite`, body);
  }

  revokeOccupant(
    id: number,
    body: RevokeHouseholdOccupantRequest,
  ): Promise<AxiosResponse<{data: HouseholdOccupantMutationResult; message?: string}>> {
    return this.post(`households/${id}/occupant/revoke`, body);
  }

  getAmenityAccessRestrictions(
    id: number,
    params: {page?: number; per_page?: number},
  ): Promise<AxiosResponse<GetListResponse<HouseholdAmenityRestrictionHistoryRow>>> {
    return axios({
      url: `${PATH}/${id}/amenity-access-restrictions`,
      method: "get",
      params: {
        page: params.page ?? 1,
        per_page: params.per_page ?? 15,
      },
    });
  }
}

export const householdApi = new HouseholdApi();
